import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { 
  Search, 
  MapPin, 
  Star,
  Building2,
  Users,
  ShoppingBag,
  Filter,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ExplorePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'courses');
  const [searchQuery, setSearchQuery] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [loading, setLoading] = useState(true);
  
  const [courses, setCourses] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    fetchData();
  }, [activeTab, stateFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (stateFilter) params.append('state', stateFilter);
      if (searchQuery) params.append('search', searchQuery);
      params.append('limit', '50');

      switch (activeTab) {
        case 'courses':
          const coursesRes = await axios.get(`${API_URL}/courses?${params}`);
          setCourses(coursesRes.data.courses);
          break;
        case 'leagues':
          const leaguesRes = await axios.get(`${API_URL}/leagues?${params}`);
          setLeagues(leaguesRes.data.leagues);
          break;
        case 'organizations':
          const orgsRes = await axios.get(`${API_URL}/organizations?${params}`);
          setOrganizations(orgsRes.data.organizations);
          break;
        case 'vendors':
          const vendorsRes = await axios.get(`${API_URL}/vendors?${params}`);
          setVendors(vendorsRes.data.vendors);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData();
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
  ];

  return (
    <div className="min-h-screen bg-bone pt-20 md:pt-24">
      {/* Header */}
      <div className="bg-deep-green py-12 md:py-16">
        <div className="container-padding">
          <h1 className="font-display font-bold text-3xl md:text-4xl text-white mb-4">
            Explore Directory
          </h1>
          <p className="text-cream/80 font-body text-lg max-w-2xl">
            Discover minority golf organizations, courses, leagues, and vendors across the nation.
          </p>
        </div>
      </div>

      <div className="container-padding py-8">
        <div className="flex gap-8">
          {/* Left Sidebar - Search & Filters (persistent on desktop) */}
          <div className="hidden lg:block w-72 shrink-0">
            <div className="sticky top-24 space-y-4">
              <div className="bg-white rounded-sm shadow-card p-5">
                <h3 className="font-display font-semibold text-deep-green mb-4">Search & Filter</h3>
                <form onSubmit={handleSearch} className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 text-sm"
                      data-testid="explore-search-input"
                    />
                  </div>
                  <select
                    value={stateFilter}
                    onChange={(e) => setStateFilter(e.target.value)}
                    className="w-full h-10 px-3 border border-input rounded-sm bg-background font-body text-sm"
                    data-testid="state-filter"
                  >
                    <option value="">All States</option>
                    {states.map((state) => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                  <Button type="submit" className="w-full btn-gold text-sm" data-testid="search-btn">
                    <Filter className="w-4 h-4 mr-2" />
                    Search
                  </Button>
                </form>
              </div>

              {/* Category Pills */}
              <div className="bg-white rounded-sm shadow-card p-5">
                <h3 className="font-display font-semibold text-deep-green mb-3 text-sm">Categories</h3>
                <div className="space-y-1">
                  {[
                    { key: 'courses', icon: MapPin, label: 'Courses' },
                    { key: 'leagues', icon: Users, label: 'Leagues' },
                    { key: 'organizations', icon: Building2, label: 'Organizations' },
                    { key: 'vendors', icon: ShoppingBag, label: 'Vendors' },
                  ].map(cat => (
                    <button
                      key={cat.key}
                      onClick={() => handleTabChange(cat.key)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm transition-colors ${
                        activeTab === cat.key 
                          ? 'bg-fairway text-white' 
                          : 'text-muted-foreground hover:bg-gray-100'
                      }`}
                      data-testid={`sidebar-tab-${cat.key}`}
                    >
                      <cat.icon className="w-4 h-4" />
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Mobile Search */}
            <div className="lg:hidden bg-white rounded-sm shadow-card p-4 mb-6">
              <form onSubmit={handleSearch} className="flex gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 text-sm" />
                </div>
                <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}
                  className="h-10 px-3 border border-input rounded-sm bg-background text-sm">
                  <option value="">All States</option>
                  {states.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <Button type="submit" className="btn-gold"><Filter className="w-4 h-4" /></Button>
              </form>
            </div>

        {/* Tabs (mobile visible, desktop hidden since sidebar handles it) */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="lg:hidden bg-white shadow-card mb-6 p-1 h-auto flex-wrap">
            <TabsTrigger value="courses" className="data-[state=active]:bg-fairway data-[state=active]:text-white py-2 px-4" data-testid="tab-courses">
              <MapPin className="w-4 h-4 mr-2" /> Courses
            </TabsTrigger>
            <TabsTrigger value="leagues" className="data-[state=active]:bg-fairway data-[state=active]:text-white py-2 px-4" data-testid="tab-leagues">
              <Users className="w-4 h-4 mr-2" /> Leagues
            </TabsTrigger>
            <TabsTrigger value="organizations" className="data-[state=active]:bg-fairway data-[state=active]:text-white py-2 px-4" data-testid="tab-organizations">
              <Building2 className="w-4 h-4 mr-2" /> Orgs
            </TabsTrigger>
            <TabsTrigger value="vendors" className="data-[state=active]:bg-fairway data-[state=active]:text-white py-2 px-4" data-testid="tab-vendors">
              <ShoppingBag className="w-4 h-4 mr-2" /> Vendors
            </TabsTrigger>
          </TabsList>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-gold" />
            </div>
          ) : (
            <>
              {/* Courses */}
              <TabsContent value="courses">
                {courses.length === 0 ? (
                  <div className="text-center py-16">
                    <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground font-body">No courses found. Try adjusting your search.</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {courses.map((course) => (
                      <Link key={course.course_id} to={`/courses/${course.course_id}`}>
                        <Card className="card-premium hover-lift h-full" data-testid={`course-${course.course_id}`}>
                          {course.images?.[0] && (
                            <div className="aspect-video overflow-hidden">
                              <img 
                                src={course.images[0]} 
                                alt={course.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          <CardContent className="p-5">
                            <div className="flex items-start justify-between mb-2">
                              <h3 className="font-display font-semibold text-lg text-deep-green line-clamp-1">
                                {course.name}
                              </h3>
                              {course.minority_owned && (
                                <Badge className="bg-gold text-deep-green shrink-0 ml-2">
                                  <Star className="w-3 h-3 mr-1" />
                                  Minority Owned
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-3">
                              <MapPin className="w-4 h-4" />
                              {course.city}, {course.state}
                            </div>
                            {course.ownership_type?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mb-3">
                                {course.ownership_type.map((type, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">
                                    {type}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            <Button size="sm" className="w-full btn-gold text-xs mt-auto" data-testid={`book-cta-${course.course_id}`}>
                              Book Tee Time
                            </Button>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Leagues */}
              <TabsContent value="leagues">
                {leagues.length === 0 ? (
                  <div className="text-center py-16">
                    <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground font-body">No leagues found. Try adjusting your search.</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {leagues.map((league) => (
                      <Link key={league.league_id} to={`/leagues/${league.league_id}`}>
                        <Card className="card-premium hover-lift h-full" data-testid={`league-${league.league_id}`}>
                          <CardContent className="p-5">
                            <h3 className="font-display font-semibold text-lg text-deep-green mb-2">
                              {league.name}
                            </h3>
                            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-3">
                              <MapPin className="w-4 h-4" />
                              {league.city}, {league.state}
                            </div>
                            {league.organizer_name && (
                              <p className="text-sm text-muted-foreground mb-3">
                                Organized by {league.organizer_name}
                              </p>
                            )}
                            {league.membership_cost && (
                              <p className="text-gold font-semibold">
                                ${league.membership_cost}/year
                              </p>
                            )}
                            {league.minority_affiliation_tags?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-3">
                                {league.minority_affiliation_tags.map((tag, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Organizations */}
              <TabsContent value="organizations">
                {organizations.length === 0 ? (
                  <div className="text-center py-16">
                    <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground font-body">No organizations found.</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {organizations.map((org) => (
                      <Card key={org.org_id} className="card-premium hover-lift h-full" data-testid={`org-${org.org_id}`}>
                        <CardContent className="p-5">
                          <Badge className="bg-fairway/10 text-fairway mb-3">
                            {org.type}
                          </Badge>
                          <h3 className="font-display font-semibold text-lg text-deep-green mb-2">
                            {org.name}
                          </h3>
                          <p className="text-sm text-muted-foreground mb-3">
                            {org.coverage} coverage
                          </p>
                          {org.website && (
                            <a 
                              href={org.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-gold hover:underline text-sm"
                            >
                              Visit Website →
                            </a>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Vendors */}
              <TabsContent value="vendors">
                {vendors.length === 0 ? (
                  <div className="text-center py-16">
                    <ShoppingBag className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground font-body">No vendors found.</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {vendors.map((vendor) => (
                      <Card key={vendor.vendor_id} className="card-premium hover-lift h-full" data-testid={`vendor-${vendor.vendor_id}`}>
                        <CardContent className="p-5">
                          <Badge className="bg-gold/10 text-gold mb-3">
                            {vendor.service_type}
                          </Badge>
                          <h3 className="font-display font-semibold text-lg text-deep-green mb-2">
                            {vendor.name}
                          </h3>
                          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-3">
                            <MapPin className="w-4 h-4" />
                            {vendor.city}, {vendor.state}
                          </div>
                          {vendor.minority_owned && (
                            <Badge className="bg-gold text-deep-green">
                              Minority Owned
                            </Badge>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </>
          )}
        </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExplorePage;
